import { computed, defineComponent, ref } from 'vue'
import type { IStickyBannerProps } from './types'
import { useStickyBannerStore, type StickyBannerObject } from './store'
import { isNotNullish } from '@/utils/is'
import Icon from '../icon'
import BellIcon from 'assets/img/svg/icons/bell.svg?url'
import XIcon from 'assets/img/svg/icons/x.svg?url'
import { tv } from 'tailwind-variants'
import { GridColumn, GridRow } from '../grid'

type BannerProps = Partial<IStickyBannerProps> & {
  close: () => void
}

const StickyBannerClass = tv({
  slots: {
    base: 'py-3 px-4 pr-12 relative shadow-lg',
    closeButton: 'cursor-pointer'
  },
  variants: {
    color: {
      primary: {
        base: 'bg-primary-500 text-white'
      },
      warning: {
        base: 'bg-findex-500 text-white'
      }
    }
  }
})

const StickyBanner = defineComponent<BannerProps>({
  name: 'StickyBanner',
  setup(props) {
    const closing = ref<boolean>(false)

    const handleClose = () => {
   //   if (closing.value) return
      closing.value = true
      props.close()
    }

    const { base, closeButton } = StickyBannerClass({ color: (props.variant || 'primary') as any })

    const bodyComponent = props.body
    return () => {
      return (
        <GridRow layout="auto" class={base()}>
          <GridColumn justifyItems="center" alignItems="center">
            {props.text ? props.text : bodyComponent ? <bodyComponent /> : ''}
          </GridColumn>
          <div
            class={closeButton()}
            onClick={handleClose}
            style={{
              position: 'absolute',
              right: '0.75rem',
              top: '0.75rem',
            }}
          >
            <Icon src={XIcon} stroke="currentColor" size="1.25rem" />
          </div>
        </GridRow>
      )
    }
  }
})

StickyBanner.props = ['close', 'animation', 'body', 'icon', 'variant', 'text']

const StickyBannerContainer = defineComponent({
  name: 'StickyBannerContainer',
  setup() {
    const store = useStickyBannerStore()

    const banners = computed((): StickyBannerObject[] => {
      return Object.values(store.stickyBanners).filter(isNotNullish)
    })

    return () => {
      return (
        <div
          style={{
            width: '100%',
            position: 'relative',
            zIndex: 10000,
            ...(banners.value.length <= 0
              ? {
                  display: 'none'
                }
              : {})
          }}
        >
          {banners.value.map((banner) => {
            const close = () => store.popBanner()

            const bannerProps: BannerProps = {
              ...banner.props,
              close,
              icon: banner.props.icon || {
                src: BellIcon,
                size: 14,
                fill: 'black'
              }
            }
            return (
              <StickyBanner
                {...bannerProps}
                style={{
                  opacity: `${banner.animation.state.value * 100}%`,
                  pointerEvents: banner.animation.state.value < 0.86 ? 'none' : 'all'
                }}
              />
            )
          })}
        </div>
      )
    }
  }
})

export default StickyBannerContainer
